/**
 * Display helpers, ported from the design's `Component` methods so the console
 * renders numbers exactly as the mock did. The bridge reports memory in bytes
 * where the mock used MiB, so conversion happens here rather than at every
 * call site.
 */

const MIB = 1024 * 1024;

export function toMiB(bytes: number): number {
  return bytes / MIB;
}

/** `512 MB` / `1.4 GB`, matching the design's `mem()`. */
export function formatMemory(bytes: number): string {
  const mib = toMiB(bytes);
  return mib >= 1024 ? `${(mib / 1024).toFixed(1)} GB` : `${mib.toFixed(0)} MB`;
}

/** Same rounding as the design: one decimal below 10%, none above. */
export function formatCpu(value: number): string {
  return `${value.toFixed(value >= 10 ? 0 : 1)}%`;
}

/** Largest whole unit only — `42s`, `18m`, `7h`, `12d`, `2mo`. */
export function formatUptime(since: number | null, now = Date.now()): string {
  if (since === null) return '—';
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

/** Seconds-since-boot, as reported by `os.uptime()`. */
export function formatDuration(seconds: number): string {
  return formatUptime(Date.now() - seconds * 1000);
}

/** `4m ago`, for alert ages. */
export function formatAgo(timestamp: number, now = Date.now()): string {
  const elapsed = formatUptime(timestamp, now);
  return elapsed === '—' ? '—' : `${elapsed} ago`;
}

export function formatClock(at: number): string {
  const date = new Date(at);
  return [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}

/** `23.4 GB` — used for totals large enough that MB would be noise. */
export function formatGiB(bytes: number): string {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function formatPercent(part: number, whole: number): string {
  if (whole <= 0) return '0';
  return ((part / whole) * 100).toFixed(0);
}

/** `3 restarts in 300s` reads better than `300 seconds`. */
export function formatSeconds(seconds: number): string {
  if (seconds < 120) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

export function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
